import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@/contexts/NavigationContext'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { WatchlistPanel } from '@/features/trading/WatchlistPanel'
import { cn } from '@/lib/cn'
import { NavIcon } from './NavIcon'
import {
  DESKTOP_PRIMARY_NAV,
  FEATURE_NAV,
  SECONDARY_NAV,
  type NavItem,
} from './nav-items'

type SidebarTab = 'menu' | 'markets'

const TAB_KEY = 'velox.sidebar_tab'

function readTab(): SidebarTab {
  try {
    const v = localStorage.getItem(TAB_KEY)
    if (v === 'menu' || v === 'markets') return v
  } catch {
    /* ignore */
  }
  return 'menu'
}

function NavButton({
  item,
  active,
  collapsed,
  onClick,
  hintKey,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  onClick: () => void
  hintKey?: string
}) {
  const { t } = useTranslation('common')
  const label = t(item.labelKey)
  const hint = hintKey ? t(hintKey) : undefined

  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={cn(
        'group relative flex w-full items-center gap-2.5 rounded-md text-start transition-all',
        collapsed ? 'h-10 justify-center px-0' : 'min-h-[2.75rem] px-2.5 py-2',
        active
          ? 'bg-accent/12 text-accent shadow-[inset_3px_0_0_0_var(--color-accent)]'
          : 'text-terminal-muted hover:bg-terminal-elevated/80 hover:text-terminal-fg',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <span
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
          active
            ? 'bg-accent/15 text-accent'
            : 'bg-terminal-elevated/70 text-terminal-muted group-hover:bg-terminal-elevated group-hover:text-terminal-fg',
        )}
      >
        <NavIcon name={item.icon} className="h-4 w-4" />
      </span>
      {!collapsed ? (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-semibold leading-tight">
            {label}
          </span>
          {hint ? (
            <span className="mt-0.5 block truncate text-[10px] font-normal leading-tight text-terminal-muted/80">
              {hint}
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  )
}

function SectionLabel({
  children,
  collapsed,
}: {
  children: string
  collapsed: boolean
}) {
  if (collapsed) {
    return <div className="my-2 border-t border-terminal-border/40" />
  }
  return (
    <p className="mb-1 mt-3 px-2.5 text-[9px] font-bold uppercase tracking-[0.14em] text-terminal-muted/70 first:mt-0">
      {children}
    </p>
  )
}

/**
 * Desktop sidebar — Menu / Markets tabs; Markets hosts the watchlist.
 * Menu is 25% narrower than prior base; Markets is 15% wider than prior markets width.
 */
export function Sidebar({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const {
    route,
    setRoute,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    setSidebarCollapsed,
  } = useNavigation()
  const [tab, setTab] = useState<SidebarTab>(readTab)

  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, tab)
    } catch {
      /* ignore */
    }
  }, [tab])

  const selectTab = (next: SidebarTab) => {
    setTab(next)
    if (next === 'markets' && sidebarCollapsed) {
      setSidebarCollapsed(false)
    }
  }

  const expandedWidth =
    tab === 'markets' ? 'w-[26.16rem]' : 'w-[13.65rem]'

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col border-e border-terminal-border/40',
        'bg-terminal-bg text-terminal-fg transition-[width] duration-200 ease-out',
        sidebarCollapsed ? 'w-14' : expandedWidth,
        className,
      )}
      aria-label="Primary"
    >
      <div
        className={cn(
          'flex h-12 items-center border-b border-terminal-border/40',
          sidebarCollapsed ? 'justify-center px-1' : 'justify-between gap-1 px-3',
        )}
      >
        {!sidebarCollapsed ? <BrandLogo height={20} /> : null}
        <button
          type="button"
          onClick={toggleSidebarCollapsed}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md',
            'text-terminal-muted hover:bg-terminal-elevated hover:text-terminal-fg',
          )}
          aria-label={
            sidebarCollapsed ? t('sidebar.expand') : t('sidebar.collapse')
          }
          title={
            sidebarCollapsed ? t('sidebar.expand') : t('sidebar.collapse')
          }
        >
          <CollapseIcon collapsed={sidebarCollapsed} />
        </button>
      </div>

      {/* Menu | Markets tabs */}
      {!sidebarCollapsed ? (
        <div className="grid shrink-0 grid-cols-2 border-b border-terminal-border/40">
          <button
            type="button"
            onClick={() => selectTab('menu')}
            className={cn(
              'h-9 rounded-none text-[11px] font-semibold transition-colors',
              tab === 'menu'
                ? 'bg-accent text-white'
                : 'bg-terminal-elevated/50 text-terminal-muted hover:text-terminal-fg',
            )}
          >
            {t('sidebar.tabMenu')}
          </button>
          <button
            type="button"
            onClick={() => selectTab('markets')}
            className={cn(
              'h-9 rounded-none text-[11px] font-semibold transition-colors',
              tab === 'markets'
                ? 'bg-accent text-white'
                : 'bg-terminal-elevated/50 text-terminal-muted hover:text-terminal-fg',
            )}
          >
            {t('sidebar.tabMarkets')}
          </button>
        </div>
      ) : (
        <div className="flex shrink-0 flex-col border-b border-terminal-border/40">
          <button
            type="button"
            title={t('sidebar.tabMenu')}
            onClick={() => selectTab('menu')}
            className={cn(
              'inline-flex h-9 w-full items-center justify-center rounded-none',
              tab === 'menu'
                ? 'bg-accent/15 text-accent'
                : 'text-terminal-muted hover:bg-terminal-elevated',
            )}
          >
            <NavIcon name="trade" className="h-4 w-4" />
          </button>
          <button
            type="button"
            title={t('sidebar.tabMarkets')}
            onClick={() => selectTab('markets')}
            className={cn(
              'inline-flex h-9 w-full items-center justify-center rounded-none',
              tab === 'markets'
                ? 'bg-accent/15 text-accent'
                : 'text-terminal-muted hover:bg-terminal-elevated',
            )}
          >
            <NavIcon name="markets" className="h-4 w-4" />
          </button>
        </div>
      )}

      {tab === 'markets' && !sidebarCollapsed ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <WatchlistPanel className="h-full border-0" />
        </div>
      ) : (
        <nav className="terminal-scroll flex flex-1 flex-col overflow-y-auto p-2">
          <SectionLabel collapsed={sidebarCollapsed}>
            {t('sidebar.sectionTrade')}
          </SectionLabel>
          <div className="flex flex-col gap-1">
            {DESKTOP_PRIMARY_NAV.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={route === item.id}
                collapsed={sidebarCollapsed}
                onClick={() => setRoute(item.id)}
                hintKey={`sidebar.hint.${item.id}`}
              />
            ))}
          </div>

          <SectionLabel collapsed={sidebarCollapsed}>
            {t('sidebar.sectionCash')}
          </SectionLabel>
          <div className="flex flex-col gap-1">
            {FEATURE_NAV.filter(
              (i) => i.id === 'deposit' || i.id === 'withdraw',
            ).map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={route === item.id}
                collapsed={sidebarCollapsed}
                onClick={() => setRoute(item.id)}
                hintKey={`sidebar.hint.${item.id}`}
              />
            ))}
          </div>

          <SectionLabel collapsed={sidebarCollapsed}>
            {t('sidebar.sectionResearch')}
          </SectionLabel>
          <div className="flex flex-col gap-1">
            {FEATURE_NAV.filter(
              (i) => i.id !== 'deposit' && i.id !== 'withdraw',
            ).map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={route === item.id}
                collapsed={sidebarCollapsed}
                onClick={() => setRoute(item.id)}
                hintKey={`sidebar.hint.${item.id}`}
              />
            ))}
          </div>

          <div className="mt-auto pt-2">
            <SectionLabel collapsed={sidebarCollapsed}>
              {t('sidebar.sectionSystem')}
            </SectionLabel>
            <div className="flex flex-col gap-1">
              {SECONDARY_NAV.map((item) => (
                <NavButton
                  key={item.id}
                  item={item}
                  active={route === item.id}
                  collapsed={sidebarCollapsed}
                  onClick={() => setRoute(item.id)}
                  hintKey={`sidebar.hint.${item.id}`}
                />
              ))}
            </div>
          </div>
        </nav>
      )}
    </aside>
  )
}

function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn(
        'h-3.5 w-3.5 transition-transform',
        collapsed && 'rotate-180',
      )}
      fill="none"
      aria-hidden
    >
      <path
        d="M10 3L6 8l4 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
